import { useIntl } from 'react-intl'
import { Button, IconStorefront, Tooltip } from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { useAnalytics } from '@/contexts/analytics'
import { type ClickOnTheListEvent } from '@/contexts/analytics/customEvents'
import { executeClickHandlers } from '@/lib/middleware'
import { getProductStatus } from '@/lib/productStatus'
import { type Product } from '@/types/product'

interface ProductQuickActionsProps {
  product: Product
}

export function ProductQuickActions({ product }: ProductQuickActionsProps) {
  const { formatMessage } = useIntl()

  const { account } = useAdmin()

  const analytics = useAnalytics()

  const viewInStoreLink = `https://${account}.myvtex.com/${product.storeUrl}/p`

  const dispatchEventOnClick =
    (action: ClickOnTheListEvent['Action']) => () => {
      analytics.track('Clicked on the list', {
        Action: action,
      })
    }

  const productIsActive = getProductStatus(product)
  const hasStoreUrl = !!product.storeUrl

  return (
    <Tooltip
      text={formatMessage({
        id:
          productIsActive !== 'inactive' && hasStoreUrl
            ? 'admin/admin.catalog.view-in-store-action-label'
            : 'admin/admin.catalog.view-in-store-quick-action-product-pending',
        defaultMessage:
          productIsActive !== 'inactive' && hasStoreUrl
            ? 'View in store'
            : 'Activate the product to make it visible in the store',
      })}
      placement="top"
    >
      <a
        href={hasStoreUrl ? viewInStoreLink : ''}
        target="_blank"
        onAuxClick={dispatchEventOnClick('Quick action: view in store')}
        onClick={executeClickHandlers(
          dispatchEventOnClick('Quick action: view in store'),
          (event) => event.stopPropagation()
        )}
        className="product-quick-action"
        aria-disabled={!hasStoreUrl}
      >
        <Button
          type="button"
          variant="neutralTertiary"
          disabled={!hasStoreUrl}
          icon={<IconStorefront />}
        />
      </a>
    </Tooltip>
  )
}
